import React, { useState } from "react";
import { Button, Flex, Input, List, Typography } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { RootState } from "../../../redux/store";
import { useAppDispatch } from "../../../redux/utils/Dispatcher";
import { exitSelectionMode } from "../../../redux/slices/chatSlice";
import {
  useSendMessageMutation,
  useSendMediaMessageMutation,
} from "../../../redux/services/chatsApi";
import { storageService } from "../../../services/storageService";
import { useChatSidebarContext } from "../../../context/ChatSidebarScreenContext";
import ChatSidebarHeader from "../../../components/common/ChatSidebarHeader";
import CustomLoader from "../../../components/common/UI/Loader";
import ContactCard from "../../../components/Chat/ContactCard";
import "./index.scss";

const { Text } = Typography;

const ForwardMessage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { chats, archivedChats, msgsToForward } = useSelector((state: RootState) => state.chat);
  const { setCurrentScreen } = useChatSidebarContext();
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedContacts, setSelectedContacts] = useState<any[]>([]);
  const [isSending, setIsSending] = useState(false);

  const [sendMessage] = useSendMessageMutation();
  const [sendMediaMessage] = useSendMediaMessageMutation();

  const userId = storageService.getUserId();

  const isContactDisabled = (contact: any): string | null => {
    if (contact.isBlocked) return "blocked";
    if (contact.isGroup && contact.groupMembers) {
      const isMember = contact.groupMembers.some(
        (m: any) => String(m._id) === String(userId),
      );
      if (!isMember) return "left";
    }
    return null;
  };

  const toggleContact = (contact: any) => {
    setSelectedContacts((prev) =>
      prev.some((c) => c.id === contact.id)
        ? prev.filter((c) => c.id !== contact.id)
        : [...prev, contact]
    );
  };

  const handleBatchForward = async () => {
    if (selectedContacts.length === 0 || msgsToForward.length === 0) return;
    setIsSending(true);

    let successCount = 0;
    let failCount = 0;
    const forwardedIds: string[] = [];

    for (const contact of selectedContacts) {
      for (const msg of msgsToForward) {
        try {
          let response: any;
          if (msg.mediaType && msg.mediaType !== 0 && msg.filePath) {
            const formData = new FormData();
            formData.append("content", msg.message || "");
            formData.append("mediaType", String(msg.mediaType));
            formData.append("mediaPath", msg.filePath);
            formData.append("chatId", contact.id);
            formData.append("isForwarded", "true");
            if (contact.isGroup) formData.append("isGroup", "true");
            else if (contact.isBroadCast) formData.append("isBroadCast", "true");
            response = await sendMediaMessage(formData).unwrap();
          } else {
            const body: any = {
              content: msg.message || "",
              chatId: contact.id,
              isForwarded: true,
            };
            if (contact.isGroup) body.isGroup = true;
            else if (contact.isBroadCast) body.isBroadCast = true;
            response = await sendMessage(body).unwrap();
          }
          const newMsgId = response?.newMessage?._id;
          if (newMsgId) forwardedIds.push(newMsgId);
          successCount++;
        } catch {
          failCount++;
        }
      }
    }

    // Persist forwarded IDs to storageService (survives refresh)
    if (forwardedIds.length > 0) {
      const existing = storageService.getForwardedMsgIds();
      const merged = [...existing, ...forwardedIds];
      storageService.setForwardedMsgIds(merged);
    }

    if (successCount > 0) {
      toast.success(`Forwarded to ${selectedContacts.length} contact(s)`);
    }
    if (failCount > 0) {
      toast.error(`Failed to forward ${failCount} message(s)`);
    }

    dispatch(exitSelectionMode());
    setCurrentScreen("chats");
    setIsSending(false);
  };

  const filteredContactList = [...chats, ...archivedChats]?.filter((contact: any) => {
    if (isContactDisabled(contact)) return false;
    const name = contact?.name?.toLowerCase() || "";
    const savedName = contact?.savedName?.toLowerCase() || "";
    const phone = contact?.sourceNumber?.toLowerCase() || "";
    const query = searchQuery.toLowerCase();
    return name.includes(query) || savedName.includes(query) || phone.includes(query);
  });

  return (
    <div className="forward-chats-container">
      <ChatSidebarHeader
        handleBackClick={() => {
          dispatch(exitSelectionMode());
          setCurrentScreen("chats");
        }}
        title={`Forward ${msgsToForward.length} message${msgsToForward.length !== 1 ? "s" : ""}`}
      />

      <div className="input-container">
        <Input
          className="custom-input"
          prefix={<SearchOutlined />}
          placeholder="Search by name or phone number..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <div className="efone-contacts-container">
        <div className="efone-contacts-list">
          <List
            className="contacts-wrapper-list"
            dataSource={filteredContactList || []}
            loading={{
              spinning: false,
              indicator: (
                <Flex className="w-full h-full" justify="center" align="center">
                  <CustomLoader />
                </Flex>
              ),
            }}
            renderItem={(contact: any) => (
              <ContactCard
                checkbox
                checked={selectedContacts.some((c) => c.id === contact.id)}
                onChecked={() => toggleContact(contact)}
                onClick={() => toggleContact(contact)}
                key={contact.id}
                userName={contact.isGroup ? contact?.name : (contact?.savedName || contact?.name || contact?.sourceNumber)}
                phoneNumber={contact?.sourceNumber}
                isEfone={contact.iseFone}
                isBroadCast={contact?.isBroadCast}
                isGroup={contact?.isGroup}
              />
            )}
          />
        </div>
      </div>

      <div className="forward-send-bar">
        <Text className="forward-send-count">
          {selectedContacts.length > 0
            ? `${selectedContacts.length} contact(s) selected`
            : "Select recipients"}
        </Text>
        <Button
          className="forward-send-btn"
          type="primary"
          disabled={selectedContacts.length === 0 || isSending}
          loading={isSending}
          onClick={handleBatchForward}
        >
          Send
        </Button>
      </div>
    </div>
  );
};

export default ForwardMessage;
